

/**
 * @fileOverview CarouselView View Module File
 *
 * The CarouselView handles the layout and swipe + next / previous interaction for a variety of carousel types.  It calculates
 * the the inline styles needed for the carousels to be responsive, and also handles subscribing to global events and recalculates
 * the needed styles on context change and window resize events.  In addition, it provides an interface for one or more
 * external controllers allowing them to move to a slide location by providing a target div ID.  It can also publish an
 * event that allows one or more controllers to run a custom callback even if the controller was not what updated the
 * carousel.
 *
 * @author jmaki
 * @contributor aranfelt
 * @version 1.0
 */

(function(context) {
    'use strict';

    var require = context.require;
    var requirejs = context.requirejs;
    var define = context.define;

    define('views/CarouselView',['require','jquery','lib/Util','models/Features','GlobalEventDispatcher','GlobalResizeListener'],function(require) {

        var $ = require('jquery');
        var Util = require('lib/Util');
        var features = require('models/Features');
        var GlobalEventDispatcher = require('GlobalEventDispatcher');
        var GlobalResizeListener = require('GlobalResizeListener');


        /**
         * Scrolling X Threshold before the carousel registers something as a 'scroll'
         *
         * @property SCROLL_X_THRESHOLD
         * @static
         * @final
         * @type {number}
         */
        var SCROLL_X_THRESHOLD = 5;

        /**
         * Scrolling Y Threshold before the carousel registers something as a 'scroll'
         *
         * @property SCROLL_Y_THRESHOLD
         * @static
         * @final
         * @type {number}
         */
        var SCROLL_Y_THRESHOLD = 12;

        /**
         * X Percentage threshold before the carousel decides to move to the scrolled slide
         *
         * @property NEXT_X_THRESHOLD_PERCENTAGE
         * @static
         * @final
         * @type {number}
         */
        var NEXT_X_THRESHOLD_PERCENTAGE = 0.10;

        /**
         * Pooled object cache for updating the margin of the DOM
         *
         * @property _positionCache
         * @type {object}
         */
        var _positionCache = {};

        /**
         * Description
         *
         * @name CarouselView
         * @class CarouselView handles the layout of the carousels
         * @constructor
         *
         * @since 1.0
         */
        var CarouselView = function(carouselId) {
            $debug('Carousel View Loaded... use ?debug=carouselView for debug messages');
            this.$carousel = $(carouselId); // A block level element that wraps around the list of slides, the 'base' pf the module
            this.$carouselList = undefined; // reference to the carousel list, the thing that animates
            this.$slides = undefined; // collection of slide elements
            this.$carouselController =  undefined; // reference to the carousel controller container;
            this.slideCount = undefined; // slideCount
            this.slideWidth = undefined; // width of the slides
            this.transitionSpeed = undefined; // transition speed
            this.$slideIndicator = undefined; // the optional text indicator for the current slide
            this.$slideTotalIndicator = undefined; // the optional text indicator for the total slides available
            this.currentSlide = 0; // the current slide index
            this.isAnimating = false; // a boolean that is set to true for the duration of a transition
            this.isLooping = false; // a boolean that indicates if the carouselView is of the looping variety
            this.isFlexible = true; // a boolean that indicates if the carouselView is of the flexible type
            this.disableOn = []; // an array of contexts on which the carousel should be disabled
            this.isEnabled = undefined; // a boolean that indicates if the carouselView is currently enabled
            this.transitionSpeed = undefined; // holds the default transition speed
            this.isFirstLoad = true; // a boolean to know the first load of carousel to handle focus on animateTo Function
            /**
             * This method gets an EventDispatcher object from the application that can be used to subscribe to events of interest
             * or publish event notifications to the rest of the application.
             *
             * @type {EventDispatcher}
             */
            this.eventDispatcher = GlobalEventDispatcher.getEventDispatcher(); // event dispatcher that will call the event, provided by the application

            /**
             *
             * @type {ResizeListener}
             */
            this.resizeListener = GlobalResizeListener.getResizeListener();
            this.init();
        };

        /**
         * Initializes the UI Component View
         * Runs a single setupHandlers call, followed by createChildren and layout
         *
         * @returns {CarouselView}
         * @since 1.0
         */
        CarouselView.prototype.init = function() {
            $debug('initializing the carouseView functions...', 'carouselView');
            // disable the view interactions until the initialization is completed
            this.isEnabled = false;

            this.transitionSpeed = 500;
            // escape early if no carousel is defined
            if (this.$carousel.length < 1) {
                return this;
            }

            /**
             * Actual threshold in pixels for when the next slide should be displayed
             *
             * @property nextThreshold
             * @type {number}
             * @default 0
             */
            this.nextThreshold = 0;

            /**
             * Flag for whether the carousel is currently being interacted with
             *
             * @property isActive
             * @type {boolean}
             * @default false
             */
            this.isActive = false;

            /**
             * Flag for whether the carousel image contents are being dragged
             *
             * @property isDraggingImage
             * @type {boolean}
             * @default false
             */
            this.isDraggingImage = false;

            /**
             * Used only when active
             * Mouse or touch's active x position
             *
             * @property tapX
             * @type {number}
             * @default 0
             */
            this.tapX = 0;

            /**
             * Used only when active
             * Mouse or touch's active y position
             *
             * @property tapY
             * @type {number}
             * @default 0
             */
            this.tapY = 0;

            /**
             * Flag for whether the carousel is considered scrolling
             * Scroll is triggered when one of the above thresholds are hit
             *
             * @property isScrolling
             * @type {boolean}
             * @default 0
             */
            this.isScrolling = false;

            /**
             * Current Scroll Offset
             * Offset in pixels from where the carousel started its activation
             * Used only when active
             *
             * @property scrollOffset
             * @type {number}
             * @default 0
             */
            this.scrollOffset = 0;

            /**
             * Starting scroll offset
             * Scroll offset that was identified when the carousel started in pixels
             * Offset should only be present when the carousel was animating when it was touched
             * Used only when active
             *
             * @property scrollOffset
             * @type {number}
             * @default 0
             */
            this.scrollStartOffset = 0;

            /**
             * Value for if mobile view was set
             * This is specifically used with the display and layout of the rG-81-Carousel
             *
             * @property mobileViewSet
             * @type {boolean}
             * @default 0
             */
            this.mobileViewSet = false;

            /**
             * set up the handlers, create child elements, layout the elements, enable the view, and go to the first slide
             */
            return this.setupHandlers()
                .createChildren()
                .layout()
                .setMobileView()
                .goToFirstSlide();
            /* ToDo Fix This */
            /* ToDo find a more appropriate place to do the goToSlide */
        };

        /**
         * Binds the scope of any handler functions, binds functions to  events via the event dispatcher
         *
         * @returns {CarouselView}
         * @since 1.0
         */
        CarouselView.prototype.setupHandlers = function() {
            // Bind event handlers scope here
            this.nextSlideHandler = this.nextSlide.bind(this);
            this.previousSlideHandler = this.previousSlide.bind(this);

            // Click/Touch Interaction Methods
            this.onTapStartHandler = this.onTapStart.bind(this);
            this.onTapMoveHandler = this.onTapMove.bind(this);
            this.onTapEndHandler = this.onTapEnd.bind(this);
            this.onImageDragHandler = this.onImageDrag.bind(this);

            // subscribe to relevant events, causing a layout when they occur
            this.eventDispatcher.subscribe(GlobalEventDispatcher.EVENTS.CONTEXT_CHANGE, this.layout.bind(this));
            this.eventDispatcher.subscribe(GlobalEventDispatcher.EVENTS.WINDOW_RESIZE, this.layout.bind(this));
            this.eventDispatcher.subscribe(GlobalEventDispatcher.EVENTS.WINDOW_RESIZE, this.resetMobile.bind(this));
            this.eventDispatcher.subscribe(GlobalEventDispatcher.EVENTS.MODAL_OPEN, this.layout.bind(this));

            return this;
        };

        /**
         * Create any child objects or references to DOM elements, also
         * Should only be run on initialization of the view
         *
         * @returns {CarouselView}
         * @since 1.0
         */
        CarouselView.prototype.createChildren = function() {
            var self = this;
            $debug('in create Children function...checking data looping ' + this.$carousel.data('isLooping'), 'carouselView');
            this.$carouselList = this.$carousel.find('.js-carouselList'); // this selector will be scoped to within the parent dom element
            this.$slides = this.$carouselList.children(); //the listItems will be the children
            this.$carouselController = this.$carousel.find('.js-heroNavigationContainer').find('.js-carouselNavigation');
            //This is for rG-50-4upTiles because the html structure is different than the other carousels
            if(this.$carouselController.length === 0){
                this.$carouselController = this.$carousel.next().find('.js-carouselNavigation');
            }
            $debug('# of slides: ' + this.$slides.length , 'CarouselView');

            if (this.$slides.length !== 0) {
                if (this.$carousel.data('isLooping') === true) {
                    this.isLooping = true;
                }

                if (this.$carousel.data('disableOn') !== undefined) {
                    this.disableOn = this.$carousel.data('disableOn');
                }

                // records the first and last slide ID's
                //updated DDS-7826 wasn't setting first and last slide when only 2 slides present
                //if (!this.$slides.eq(1).attr('id') || !$this.$slides.last().attr('id')) {

                if (this.$slides.eq(0).attr('id') || this.$slides.last().attr('id')) {
                    this.firstSlideId = '#' + this.$slides.first().attr('id').toString();
                    this.lastSlideId = '#' + this.$slides.last().attr('id').toString();
                    $debug('first slide : ' + this.firstSlideId, 'CarouselView');
                    $debug('Last slide : ' + this.lastSlideId, 'CarouselView');
                }

                // Set tabindex to access to focus by JS but before createLoopSlides
                // To not access the slides that are not accessible by the user (faux slides)
                this.$slides.attr('tabindex','-1'); 
                this.$slides.attr('aria-hidden',true);

                if (this.isLooping) {
                    this.createLoopSlides();
                }

                this.$slides = this.$carouselList.children(); //the listItems will be the children
                this.$anchors = this.$slides.find('a').attr('tabindex', '-1'); // disable all <a> inside slides, the only one with focus access is the <a> inside active slide

                // create references to the triggers
                this.$triggerPrevious = this.$carousel.find('.js-carouselTrigger_left');
                this.$triggerNext = this.$carousel.find('.js-carouselTrigger_right');
                                
                this.slideCount = this.$slides.length; // the total number of slides, before appending or pre-pending slides used in looping
                this.slideWidth = this.$slides.width(); // records the width of the slides

                //create references to the text indicators, if present
                this.$slideIndicator = this.$carousel.find('.js-carouselIndex');
                this.$slideTotalIndicator = this.$carousel.find('.js-slideTotal');

                // sets a flag to determine in which direction the slides should animate
                this.shouldUseRTL = $('body').hasClass('rtl');

                // determines if this is a hero slide
                if (this.$carousel.data('isFlexible') === true) {
                    this.isFlexible = true;
                }

                // if this is a hero slide, setSlideWith
                if (this.isFlexible) {
                    this.setHeroSlideWidth();
                }

            } else {
                $debug('carousel was empty...', 'carouselView');
            }

            return this;
        };


        CarouselView.prototype.resetLayout = function() {
            var animationProp = 'marginLeft';
            if (this.shouldUseRTL) {
                animationProp = 'marginRight';
            }
            this.$carouselList.width('');
            this.$carouselList.css('min-width', '');
            this.$slides.css('width', '');
            this.$carouselList.css(animationProp, '');
            if (this.$fauxFirstSlide) {
                this.$fauxFirstSlide.addClass('MMM--fauxSlide');
            }

            if (this.$fauxLastSlide) {
                this.$fauxLastSlide.addClass('MMM--fauxSlide');
            }
        };

        /* setMobileView function only to be used with rG-81-Carousel */
        CarouselView.prototype.setMobileView = function() {
            if (this.$carousel.hasClass('rG-81-CarouselContainer') && this.resizeListener.getCurrentContext() === "smallContext") {
                $('.MMM--fauxSlide').next().addClass('MMM--activeContentList');

                // Initialize the width of each content container
                this.$carousel.find('ul.MMM--contentList_sliderContent').each(function() {
                    var sliderContent = $(this);
                    var size = $(this).find('li.js-contentList-item').length;
                    var totalWidth = $(window).width() * size;
                    $(this).css('width', totalWidth + "px");

                    // Initialize the width of each content item in list
                    $(this).find('li.js-contentList-item').each(function () {
                        $(this).css('width', totalWidth / size + "px");
                    })

                    // Add navigation dots for each content item
                    var containerPlacement = $(this).parent().parent().parent().find('li.MMM--activeContentList').find('.MMM--tileContent-inner-bd').height() + 46; 
                    var largeImgClass = $(this).find('.MMM--mediaListFigure .MMM--largeImgClass').length > 0 ? 'MMM--largeCarouselItem' : '';
                    $(this).after("<div class='MMM--heroNavigationContainer MMM--isOnMobile' style='width: " + totalWidth / size + 'px; top: -' + containerPlacement +"px;'><ul class='MMM--heroCarouselNavigation MMM--hList js-carouselNavigation " + largeImgClass + "'></ul></div");
                    for (var i = 0; i < size; i ++) {
                        if (i == 0) {
                            $(this).next().children('ul.MMM--heroCarouselNavigation').append("<li class='MMM--heroCarouselNavigation-item'><a href='#" + i * (totalWidth / size) + "' class='js-mobileCarouselNavDot MMM--isActive js-carouselItem-" + i + "' data-carousel-item='" + i + "'><span class='MMM--icn MMM--icn_circle'></span></a></li>");
                        }
                        else {
                            $(this).next().children('ul.MMM--heroCarouselNavigation').append("<li class='MMM--heroCarouselNavigation-item'><a href='#" + i * (totalWidth / size) + "' class='js-mobileCarouselNavDot js-carouselItem-" + i + "'  data-carousel-item='" + i + "'><span class='MMM--icn MMM--icn_circle'></span></a></li>");
                        }
                    }
                    
                    // Add functionality for the dot-navigation
                    var originalMargin = $(this).css('margin-left').replace("px", "");
                    originalMargin = originalMargin.indexOf("%") !== -1 ? originalMargin.replace("%","") * 0.01 * totalWidth : originalMargin; 
                    $(this).next().find('a.js-mobileCarouselNavDot').each(function () {
                        $(this).unbind("click keydown").on("click keydown", function(event) {
                            if (event.which == 1 || event.which == 13) { // verify that it was a click (1) or enter keydown (13)
                                event.preventDefault();

                                // Set margin for carousel
                                var setMargin = originalMargin - $(this).attr('href').replace("#", "");
                                sliderContent.css('margin-left', setMargin + "px");

                                // Update aria-hidden and focus states
                                var carouselItem = $(this).data("carousel-item");
                                sliderContent.children().find('a').attr('tabindex', '-1');
                                sliderContent.children().eq(carouselItem).find('a').attr('tabindex', '0').first().focus();

                                // Update the MMM--isActive
                                $(this).parent().siblings().children('.MMM--isActive').removeClass('MMM--isActive');
                                $(this).addClass('MMM--isActive');
                            }
                        })
                    })

                    // Add current focus state
                    sliderContent.find('a').attr('tabindex', '-1');
                    sliderContent.children().first().find('a').attr('tabindex', '0');
                })

                // Left and right arrow navigation through content
                $('.js-MobileCarouselRight').each(function() {
                    // Modify placement if carousel contains thumbnail images
                    if ($(this).siblings('ul.js-carouselList').find('.MMM--img_lrgThumbnail.MMM--tileContent').length > 0) {
                        var rightStyle = (($(this).parent().width() - 100) / 2) - 30;
                        $(this).css('right', rightStyle + "px");
                        $(this).removeClass('mix-MMM--icn_white');
                        $(this).addClass('mix-MMM--icn_dark');
                    } else {
                        if ($(this).parent().width() > 400) {
                            var rightStyle = (($(this).parent().width() - 400) / 2) + 10;
                            $(this).css('right', rightStyle + "px");
                        }
                    }

                    // Add functionality
                    $(this).unbind("click keydown").on('click keydown', function (event) {
                        if (event.which == 1 || event.which == 13) { // verify that it was a click (1) or enter keydown (13)
                            var _self = $(this);
                            var currentList = _self.siblings().find('.MMM--activeContentList').find('.MMM--contentList_sliderContent');
                            var listCount = currentList.find('li.js-contentList-item').length;
                            var size = currentList.width() / listCount;
                            var newMargin = (currentList.css('margin-left').replace("px", "") - (2 * size)); //(0.5 * currentList.width()));
                            if (-1 * newMargin > currentList.width()) {
                                currentList.css('margin-left', '+=' + ((listCount - 1) * size) + "px");//0.75 * currentList.width() +"px");
                            }
                            else {
                                currentList.css('margin-left', newMargin + size + "px");//(0.25 * currentList.width()) + "px");
                            }

                            // Update active dot position
                            var currentDot = currentList.siblings('.MMM--heroNavigationContainer').find('.MMM--isActive');
                            currentDot.removeClass('MMM--isActive');
                            var carouselItem = -1; // Default is none
                            if (currentDot.parent().next().length > 0) {
                                currentDot.parent().next().children('a.js-mobileCarouselNavDot').addClass('MMM--isActive');
                                carouselItem = currentDot.parent().next().children('a.js-mobileCarouselNavDot.MMM--isActive').data("carousel-item");
                            } else {
                                currentDot.parent().siblings().first().children('a.js-mobileCarouselNavDot').addClass('MMM--isActive');
                                carouselItem = currentDot.parent().siblings().first().children('a.js-mobileCarouselNavDot.MMM--isActive').data("carousel-item");
                            }

                            // Update aria-hidden and focus states
                            currentList.children().find('a').attr('tabindex', '-1');
                            currentList.children().eq(carouselItem).find('a').attr('tabindex', '0').first().focus();
                        }
                    })
                });
                $('.js-MobileCarouselLeft').each(function() {
                    // Modify placement if carousel contains thumbnail images
                    if ($(this).siblings('ul.js-carouselList').find('.MMM--img_lrgThumbnail.MMM--tileContent').length > 0) {
                        var leftStyle = (($(this).parent().width() - 100) / 2) - 30;
                        $(this).css('left', leftStyle + "px");
                        $(this).removeClass('mix-MMM--icn_white');
                        $(this).addClass('mix-MMM--icn_dark');
                    } else {
                        if ($(this).parent().width() > 400) {
                            var leftStyle = (($(this).parent().width() - 400) / 2) + 10;
                            $(this).css('left', leftStyle + "px");
                        }
                    }

                    // Add functionality
                    $(this).unbind("click keydown").on('click keydown', function (event) {
                        if (event.which == 1 || event.which == 13) { // verify that it was a click (1) or enter keydown (13)
                            var _self = $(this);
                            var currentList = _self.siblings().find('.MMM--activeContentList').find('.MMM--contentList_sliderContent');
                            var listCount = currentList.find('li.js-contentList-item').length;
                            var size = currentList.width() / listCount;
                            if (currentList.css('margin-left') >= "0px") {
                                // 1 - the child width
                                currentList.css('margin-left', '-=' + ((listCount - 1) * size) + "px");
                            }
                            else {
                                currentList.css('margin-left', '+=' + size +"px");
                            } 

                            // Update active dot position
                            var currentDot = currentList.siblings('.MMM--heroNavigationContainer').find('.MMM--isActive');
                            currentDot.removeClass('MMM--isActive');
                            var carouselItem = -1; // Default is none
                            if (currentDot.parent().prev().length > 0) {
                                currentDot.parent().prev().children('a.js-mobileCarouselNavDot').addClass('MMM--isActive');
                                carouselItem = currentDot.parent().prev().children('a.js-mobileCarouselNavDot.MMM--isActive').data("carousel-item");
                            } else {
                                currentDot.parent().siblings().last().children('a.js-mobileCarouselNavDot').addClass('MMM--isActive');
                                carouselItem = currentDot.parent().siblings().last().children('a.js-mobileCarouselNavDot.MMM--isActive').data("carousel-item");
                            }

                            // Update aria-hidden and focus states
                            currentList.children().find('a').attr('tabindex', '-1');
                            currentList.children().eq(carouselItem).find('a').attr('tabindex', '0').first().focus();
                        }
                    })
                });

                /* Mobile navigation links (i.e. 1-6) */
                $('.js-carouselNavigationMobile').each(function() {
                    // Adjust height to be above sets
                    if ($(this).parent().siblings('ul.js-carouselList').find('.MMM--largeImgClass').length > 0) {
                        var topStyle = ($(window).width() < 345) ? '-275px' : '-292px';
                        topStyle = ($(window).width() > 380 && $(window).width() < 405) ? '-310px' : topStyle;
                        $(this).css('top', topStyle);
                    } else {
                        if ($(this).css('top') === '0px') {$(this).css('top', '-218px');}
                    }
                    var leftStyle = ($(this).parent().parent().width() - $(this).width) / 2;
                    $(this).css('left', leftStyle + "px");

                    // Add event listeners to mobile links
                    $(this).find('a.js-mobileNavLink').each(function() {
                        $(this).unbind("click keydown").on('click keydown', function (event) {
                            if (event.which == 1 || event.which == 13) { // verify that it was a click (1) or enter keydown (13)
                                event.preventDefault();
                                var _self = $(this);
                                var navToSeries = (_self.attr('href') !== undefined) ? _self.attr('href').replace("#", "") : "";
                                $("#" + navToSeries).siblings('.MMM--isActive').attr('aria-hidden', 'true');
                                $("#" + navToSeries).siblings('.MMM--activeContentList').removeClass('MMM--activeContentList');
                                $("#" + navToSeries).siblings('.MMM--isActive').removeClass('MMM--isActive');
                                $("#" + navToSeries).addClass('MMM--activeContentList');
                                $("#" + navToSeries).addClass('MMM--isActive');
                                $("#" + navToSeries).attr('aria-hidden', 'false');

                                // Update aria and focus states
                                var carouselItem = $("#" + navToSeries).find('a.js-mobileCarouselNavDot.MMM--isActive').data("carousel-item");
                                $("#" + navToSeries).find('ul.MMM--contentList_sliderContent a').attr('tabindex', '-1');
                                $("#" + navToSeries).find('ul.MMM--contentList_sliderContent').children().eq(carouselItem).find('a').attr('tabindex', '0').first().focus();
                                _self.parent().siblings().children('a.js-mobileNavLink').attr('aria-current', 'false');
                                _self.parent().siblings().children('a.js-mobileNavLink').removeClass("MMM--isActive");
                                _self.attr('aria-current', 'true');
                                _self.addClass('MMM--isActive');
                            }
                        })
                    })
                })
                this.mobileViewSet = true;
                this.setGeneralCarousel();
            }

            return this;
        };

        CarouselView.prototype.resetMobile = function() {
            if (!this.mobileViewSet) {
                this.setMobileView();
            }
            else {
                if (this.$carousel.hasClass('rG-81-CarouselContainer') && this.resizeListener.getCurrentContext() === "smallContext") {
                    
                    // Update Carousel Sizing
                    this.$carousel.find('ul.MMM--contentList_sliderContent').each(function() {
                        var size = $(this).find('li.js-contentList-item').length;
                        var totalWidth = $(window).width() * size;
                        $(this).css('width', totalWidth + "px");

                        // Initialize the width of each content item in list
                        $(this).find('li.js-contentList-item').each(function () {
                            $(this).css('width', totalWidth / size + "px");
                        })

                        // Update href values for navigation dots
                        var containerPlacement = $(this).parent().parent().parent().find('li.MMM--activeContentList').find('.MMM--tileContent-inner-bd').height() + 46; 
                        $(this).next('div.MMM--heroNavigationContainer.MMM--isOnMobile').css('width',  totalWidth / size + 'px');
                        $(this).next('div.MMM--heroNavigationContainer.MMM--isOnMobile').css('top',  '-' + containerPlacement +'px');
                        for (var i = 0; i < size; i ++) {
                            var newHrefValue = '#' + (i * (totalWidth / size));
                            $(this).next('div.MMM--heroNavigationContainer.MMM--isOnMobile').children('ul.js-carouselNavigation').children().eq(i).children('a').attr('href', newHrefValue);
                        }

                        // Update the margin of the carousel
                        var activeDot = $(this).next('div.MMM--heroNavigationContainer.MMM--isOnMobile').children('ul.js-carouselNavigation').find('a.js-mobileCarouselNavDot.MMM--isActive');
                        if ( $(activeDot).attr('href') !== undefined ) {
                            $(this).css('margin-left', '-' + $(activeDot).attr('href').replace('#', '') + 'px');
                        }
                    });

                    // Update Mobile Link Navigation positions
                    $('.js-carouselNavigationMobile').each(function() {
                        // Adjust height to be above sets
                        if ($(this).parent().siblings('ul.js-carouselList').find('.MMM--largeImgClass').length > 0) {
                            var topStyle = ($(window).width() < 345) ? '-275px' : '-292px';
                            topStyle = ($(window).width() > 380 && $(window).width() < 405) ? '-310px' : topStyle;
                            $(this).css('top', topStyle);
                        } else {
                            if ($(this).css('top') === '0px') {$(this).css('top', '-208px');}
                        }
                        var leftStyle = ($(this).parent().parent().width() - $(this).width) / 2;
                        $(this).css('left', leftStyle + "px");
                    })

                    // Update Left Arrow Positions
                    $('.js-MobileCarouselLeft').each(function() {
                        // Modify placement if carousel contains thumbnail images
                        if ($(this).siblings('ul.js-carouselList').find('.MMM--img_lrgThumbnail.MMM--tileContent').length > 0) {
                            var leftStyle = (($(this).parent().width() - 100) / 2) - 30;
                            $(this).css('left', leftStyle + "px");
                        } else {
                            if ($(this).parent().width() > 400) {
                                var leftStyle = (($(this).parent().width() - 400) / 2) + 10;
                                $(this).css('left', leftStyle + "px");
                            }
                        }
                    });

                    // Update Right Arrow Positions
                    $('.js-MobileCarouselRight').each(function() {
                        // Modify placement if carousel contains thumbnail images
                        if ($(this).siblings('ul.js-carouselList').find('.MMM--img_lrgThumbnail.MMM--tileContent').length > 0) {
                            var rightStyle = (($(this).parent().width() - 100) / 2) - 30;
                            $(this).css('right', rightStyle + "px");
                        } else {
                            if ($(this).parent().width() > 400) {
                                var rightStyle = (($(this).parent().width() - 400) / 2) + 10;
                                $(this).css('right', rightStyle + "px");
                            }
                        }
                    });
                }
            }

            return this;
        }


        CarouselView.prototype.setGeneralCarousel = function() {
            // Update navigation location on non-mobile screens
            if (this.context !== "smallContext") {
                $('.rG-81-Carousel ul.js-carouselNavigation').css('left', 'calc(50% - ' + (0.5 * $('.rg-81-Carousel ul.js-carouselNavigation').width()) + "px)");
                $('.rG-81-Carousel ul.js-carouselNavigation').css('top', '0px');
            }

            // Add aria-labels for mobile number navigation and tablet/desktop navigation
            $('.js-mobileNavLink').each(function() {
                var ariaLabel = $(this).parent().parent().attr('data-aria') + " " + $(this).children('.MMM--mobileNavLink').html();
                $(this).attr('aria-label', ariaLabel);
            });

            // Set first navigation item as current and active
            $('ul.js-carouselNavigation').children().first().children().first('a').addClass('MMM--isActive');
            $('ul.js-carouselNavigation').children().first().children().first('a').attr('aria-current', 'true');
            $('li.MMM--activeContentList').each(function(){
                $(this).attr('aria-hidden', 'false');
                $(this).find('ul.MMM--contentList_sliderContent').children().first().find('a').attr('tabindex', '0');
            });
            return this;
        }

        /**
         * Performs measurements and applies any positioning style logic
         * Should be run anytime the parent layout changes
         *
         * @returns {CarouselView}
         * @since 1.0
         */
        CarouselView.prototype.layout = function() {
            var context = this.resizeListener.getCurrentContext();
            this.context = context;

            $debug('in layout function...', 'carouselView');
            /*
             * Reset the trigger states
             * */

            if (this.$slides.length !== 0) {
                if (this.$triggerNext !== undefined){
                    this.$triggerNext.removeClass('MMM--triggerInactive');
                }
                if (this.$triggerPrevious !== undefined){
                    this.$triggerPrevious.removeClass('MMM--triggerInactive');
                }
                if ($.inArray(context, this.disableOn) !== -1) {
                    this.disable();
                    this.resetLayout();
                    return this;
                }

                if (this.isFlexible) {
                    this.setHeroSlideWidth();
                }

                if (this.$fauxFirstSlide !== undefined) {
                    this.$fauxFirstSlide.removeClass('fauxSlide');
                }

                if (this.$fauxLastSlide !== undefined) {
                    this.$fauxLastSlide.removeClass('fauxSlide');
                }

                this.enable();
                var slideWidth = this.$carousel.outerWidth(); // determines the width of each slide returns a number
                var totalListWidth = this.slideCount * slideWidth; // determines the total width the slide list needs to be to not wrap
                this.$carouselList.width(totalListWidth);
                this.$carouselList.css('minWidth', totalListWidth);
                this.slideWidth = this.$slides.outerWidth(); //update the slide width
                this.$slideIndicator.text(this.currentSlide + 1);
                this.$slideTotalIndicator.text(this.slideCount);

                // this adds a class to the UL that can then be used to set the list style in IE, corrects known IE bug
                this.$carouselList.addClass('isInitialized_ie');
                //        this.onLayout();



                if (this.currentSlide === this.slideCount - 1 && this.isLooping === false) {
                    this.$triggerNext.addClass('MMM--triggerInactive');
                }

                if (this.currentSlide === 0 && this.isLooping === false) {
                    this.$triggerPrevious.addClass('MMM--triggerInactive');
                }

                // Calculate the next threshold based on the current slide width
                this.nextThreshold = this.slideWidth * NEXT_X_THRESHOLD_PERCENTAGE;
                
                this.dynamicAlignmentForMobile();
            } else {
                $debug('no slides...skipping the layout...', 'carouselView');
            }

            return this;
        };

        /**
         * Dynamic Alignment for mobile.
         * This method will align the arrows and the navigation container for the carousel dymically
         * due to, we must allow dynamic images heights, and the html structure doesn't allow us 
         * to align the content with just css
         *
         * @return {*}
         */
        CarouselView.prototype.dynamicAlignmentForMobile = function(){
            if(this.$slides.find('.MMM--heroStaticFigure img').length !== 0 && this.resizeListener.getCurrentContext() === "smallContext"){
                // Get the max height of images
                var imgHeights = this.$slides.find('.MMM--heroStaticFigure img').map(function( i, e ) {
                    return $( e ).height();
                }).get();
                var maxHeight = Math.max.apply(this, imgHeights);

                //Set gradients height
                this.$slides.find('.MMM--heroStaticFigure-gradient').css('height',maxHeight + 'px');

                //Set navigation container top
                // It calculates the max height of the images and subtracts the height of the navigation container to use it as top value
                var heroNavigationContainerHeight = this.$carousel.find('.MMM--heroNavigationContainer').height();
                var navigationTop = maxHeight - heroNavigationContainerHeight;
                this.$carousel.find('.MMM--heroNavigationContainer').css('top', navigationTop + 'px');

                //Set arrows top
                // it calculates the max height of the images and substracts the height of the arrow, also substract 10px of padding
                // and divide it between 2 to get the top position of the arrow
                var heroCarouselTriggerHeight = this.$carousel.find('.MMM--heroCarouselTrigger').height();
                var arrowsTop = (maxHeight - heroCarouselTriggerHeight - 10) / 2;
                this.$carousel.find('.MMM--heroCarouselTrigger').css('top',arrowsTop);
                
            }else if(this.$slides.find('.MMM--heroStaticFigure img').length !== 0  && this.resizeListener.getCurrentContext() !== "smallContext"){
                //Set height and top for the other context that are not mobile
                this.$slides.find('.MMM--heroStaticFigure-gradient').css('height','100%');

                this.$carousel.find('.MMM--heroNavigationContainer').css('top', 'auto');

                this.$carousel.find('.MMM--heroCarouselTrigger').css('top','0');
            }
        }

        /**
         * Sets the slide width to match width of "view" container
         * @return {*}
         */
        CarouselView.prototype.setHeroSlideWidth = function() {
            var newSlideWidth = this.$carousel.width();
            this.$slides.css('width', newSlideWidth);

            return this;
        };
        
        /**
         * creates a slide at the begining and end of the slideshow that will visually create the illusion of the list looping
         *
         * @return {*}
         */
        CarouselView.prototype.createLoopSlides = function() {
            var fauxFirstSlideContent = this.$slides.first().html();
            var fauxLastSlideContent = this.$slides.last().html();

            this.$fauxFirstSlide = $('<li>' + fauxFirstSlideContent + '</li>');
            this.$fauxLastSlide = $('<li>' + fauxLastSlideContent + '</li>');

            // remove ID's from these two slides
            this.$fauxFirstSlide.removeAttr('id')
                .addClass('MMM--carouselListItem_hero');

            this.$fauxLastSlide.removeAttr('id')
                .addClass('MMM--carouselListItem_hero');

            this.$fauxFirstSlide.find('.MMM--heroContentWrapper').remove();
            this.$fauxLastSlide.find('.MMM--heroContentWrapper').remove();

            this.$carouselList.prepend(this.$fauxLastSlide);
            this.$carouselList.append(this.$fauxFirstSlide);


            return this;
        };

        /**
         * Used to go to the first slide, checks to see if the CarouselView is Looping and then goes to the true first slide
         * cache the transition speed, then set it to zero and jump to the target frame, and set the transition back to
         * whatever it was when the method was called
         *
         * @return {CarouselView}
         */
        CarouselView.prototype.goToFirstSlide = function() {
            //if (this.isEnabled === false || this.$slides.length !== 0) { MDPL-1084-plz refer to the comments section of the ticket
            if (this.isEnabled === false) {
                return this;
            }
            var originalTransition = this.transitionSpeed;
            this.transitionSpeed = 0;
            console.log("go to slide" + this.firstSlideId)
            this.goToSlide(this.firstSlideId);
            this.isFirstLoad = false; //After the first Go to slide (animateTo) disable firstLoad
            this.transitionSpeed = originalTransition;
            this.updateControllers();

            return this;
        };

        /**
         * Used to jump to the last slide
         *
         * cache the transition speed, then set it to zero and jump to the target frame, and set the transition back to
         * whatever it was when the method was called
         *
         * @return {*}
         */
        CarouselView.prototype.goToLastSlide = function() {
            var originalTransition = this.transitionSpeed;
            this.transitionSpeed = 0;
            this.goToSlide(this.lastSlideId);
            this.transitionSpeed = originalTransition;
            this.updateControllers();

            return this;
        };

        /**
         * Function to set the current slide, returns early if the index passed is out of the range, this is exposed
         * to the CarouselViewController so other elements on a page can set the position of the carousel.
         * @param index {int}
         * @return {*}
         */
        CarouselView.prototype.setCurrentSlide = function(index) {
            
            
            if (index > this.slideCount || index < 0) {
                return this;
            }
            if (this.$slides !== undefined){
                this.$slides.attr('aria-hidden',true);
                this.$slides.find('a').attr('tabindex','-1');
                this.$slides.removeClass('MMM--isActive');
            }

            $(this.$slides[index]).addClass('MMM--isActive');
            $(this.$slides[index]).removeAttr('aria-hidden');                           
            $(this.$slides[index]).find('a').attr('tabindex','0');

            this.currentSlide = index;
            //console.log('set curre slide ' +this.currentSlide);
            this.animateTo(index);
            this.updateControllers();
            return this;
        };

        /**
         *  Looks through the current slides to see which slide is active
         *  ToDo make this check for the current active slide by looking at the current slide property, do not store state
         *  in the DOM
         */
        CarouselView.prototype.getCurrentSlideID = function() {
            return this.$slides.filter('.MMM--isActive').attr('id');
        };

        /**
         * Sends a message to any controllers looking for an update message, through the event dispatcher
         */
        CarouselView.prototype.updateControllers = function() {
            this.eventDispatcher.publish(GlobalEventDispatcher.EVENTS.CAROUSEL_UPDATE);
        };

        /**
         * Attempts to move the current slide to the nextSlide on the list, if the current position is at the end of the
         * list then it loops back to the beginning.  It then calls the setCurrentSlide function.
         * @return {CarouselView}
         */
        CarouselView.prototype.nextSlide = function() {
            //if the current slide is not the last slide, enable triggers and update the current slide
           

            if (this.currentSlide < this.slideCount - 1) {
                this.enableTriggers();
                this.currentSlide = this.currentSlide + 1;
            }

            //set current slide to new value
            this.setCurrentSlide(this.currentSlide);

            if (this.currentSlide === this.slideCount - 1 && this.isLooping === false) {
                this.$triggerNext.addClass('MMM--triggerInactive');
            }

            this.updateControllers();

            return this;
        };
        /**
         * Attempts to move the current slide to the previousSlide on the list, if the current position is at the beginning of
         * the list, it goes to the end. It then calls the setCurrentSlide function.
         * @return {*}
         */
        CarouselView.prototype.previousSlide = function() {
            
            if (this.currentSlide > 0) {
                this.enableTriggers();
                this.currentSlide = this.currentSlide - 1;
            }

            this.setCurrentSlide(this.currentSlide);

            if (this.currentSlide === 0 && this.isLooping === false) {
                this.$triggerPrevious.addClass('MMM--triggerInactive');
            }

            return this;
        };

        CarouselView.prototype.enableTriggers = function() {
            if (this.$triggerNext !== undefined){
                this.$triggerNext.removeClass('MMM--triggerInactive');
            }

            if (this.$triggerPrevious !== undefined){
                this.$triggerPrevious.removeClass('MMM--triggerInactive');
            }
            return this;
        };

        /**
         * This is a method that allows the carousel to animate to a specific slide based on ID
         * @param {String || jQuery} slideId
         */
        CarouselView.prototype.goToSlide = function(slideId) {
            // Do not apply to mobile view in rG-81-Carousel

            if (!(this.resizeListener.getCurrentContext() === "smallContext" && this.$carousel.hasClass('rG-81-CarouselContainer'))) {

                if (slideId !== undefined) {
                    slideId = slideId.toString(); //ensure slideId is always a string
                }


                if (slideId !== undefined) {
                    slideId = slideId.toString(); //ensure slideId is always a string
                }

                slideId = slideId ? slideId.replace("#", "") : slideId;
                var $slideElement = slideId || this.$slides.eq(1).attr("id");
                if (this.$slides !== undefined){
                    this.$slides.removeClass('MMM--isActive');
                }
                $("#" + $slideElement).addClass('MMM--isActive');
                try {
                    if ($("#" + $slideElement).length === 0) {
                        throw 'Slide ' + $slideElement + ' not found!';
                    }
                } catch (error) {
                    if (window.console) {
                        window.console.log(error);
                    }
                    return;
                }
                var indexOfSlide = this.$slides.index($("#" + $slideElement));
                
                this.setCurrentSlide(indexOfSlide);
            }
        };

        /**
         * Enables the view
         * Performs any event binding to handlers
         * Exits early if it is already enabled
         *
         * @returns {CarouselView}
         * @since 1.0
         */
        CarouselView.prototype.enable = function() {
            if (this.isEnabled) {
                return this;
            }
            var self = this;
            this.isEnabled = true;

            this.$carousel.on(features.DOWN, this.onTapStartHandler);

            this.$triggerNext.on('click keydown', function(e) {
                if (e.which == 1 || e.which == 13) { // verify that it was a click (1) or enter keydown (13)
                    if (e.preventDefault) {
                        e.preventDefault();
                    } else {
                        //needed for IE Support
                        e.returnValue = false;
                    }
                    self.nextSlideHandler();
                }
            });

            this.$triggerPrevious.on('click keydown', function(e) {
                if (e.which == 1 || e.which == 13) { // verify that it was a click (1) or enter keydown (13)
                    if (e.preventDefault) {
                        e.preventDefault();
                    } else {
                        //needed for IE Support
                        e.returnValue = false;
                    }
                    self.previousSlideHandler();
                }
            });

            return this;
        };

        /**
         * Disables the view
         * Tears down any event binding to handlers
         * Exits early if it is already disabled
         *
         * @returns {CarouselView}
         * @since 1.0
         */
        CarouselView.prototype.disable = function() {
            if (!this.isEnabled) {
                return this;
            }

            this.isEnabled = false;

            // Tear down any event handlers
            this.$carousel.off(features.DOWN, this.onTapStartHandler);

            return this;
        };

        /**
         * Destroys the view
         * Tears down any events, handlers, elements
         * Should be called when the object should be left unused
         *
         * @returns {CarouselView}
         * @since 1.0
         */
        CarouselView.prototype.destroy = function() {
            this.disable();

            return this;
        };

        CarouselView.prototype.animateTo = function(index) {
            var width = this.$slides.width();
            var animateDistance = index * width;
            var self = this;
            var animationProp = {};

            if (this.shouldUseRTL) {
                animationProp = {
                    'marginRight': -animateDistance
                };
            } else {
                animationProp = {
                    'marginLeft': -animateDistance
                };
            }

            try {
                if (index > this.slideCount || index < 0) {
                    throw 'Error: slide number ' + index + ' does not exist';
                }
            } catch (error) {
                if (window.console) {
                    window.console.log(error);
                }
                return;
            }

            this.isAnimating = true;
            //ToDo see if there is a cleaner way to do this
            if (this.isLooping && index === 0) {
                self.$carouselList.stop()
                    .animate(animationProp,
                this.transitionSpeed,

                function() {
                    self.isAnimating = false;
                    self.$slideIndicator.text(self.currentSlide + 1);
                    self.goToLastSlide();
                    
                });
            } else if (this.isLooping && index === this.slideCount - 1) {
                self.$carouselList.stop()
                    .animate(animationProp,
                this.transitionSpeed,

                function() {
                    self.isAnimating = false;
                    self.$slideIndicator.text(self.currentSlide + 1);
                    self.goToFirstSlide();
                });
            } else {
                self.$carouselList.stop()
                    .animate(animationProp,
                this.transitionSpeed,

                function() {
                    self.isAnimating = false;
                    self.$slideIndicator.text(self.currentSlide + 1);
                    if(!self.isFirstLoad) $(self.$slides[self.currentSlide]).focus();
                });
            }
        };

        /*******
         * Click/Touch Interactive Methods
         *******/

        /**
         * Activates the scroll action
         * Used to turn on any listeners after the carousel has been tapped
         *
         * @method activate
         * @chainable
         */
        CarouselView.prototype.activate = function() {
            if (this.isActive) {
                return this;
            }

            this.isActive = true;
            if (this.$carousel.find('.MMM--isActive .js-360Player').length < 1) {
                this.$carousel.on(features.MOVE, this.onTapMoveHandler);
            }
            this.$carousel.on(features.UP, this.onTapEndHandler);
            this.$carousel.on(features.CANCEL, this.onTapEndHandler);

            // Mobile IE
            // mouseleave is triggering and causing all mouse interaction to fail
            // Not yet fixed, but a possible solution would be to start listening to the document
            // And do manual testing based on mouse movements and hit tests
            if (features.useLeaveInteraction) {
                this.$carousel.on(features.LEAVE, this.onTapEndHandler);
            }
            this.$carousel.on('dragstart', 'img', this.onImageDragHandler);

            return this;
        };

        /**
         * Deactivates the scroll action
         * Used to turn off any listeners after the carousel has finished being tapped
         *
         * @method deactivate
         * @chainable
         */
        CarouselView.prototype.deactivate = function() {
            if (!this.isActive) {
                return this;
            }

            this.isActive = false;
            this.$carousel.off(features.MOVE, this.onTapMoveHandler);
            this.$carousel.off(features.UP, this.onTapEndHandler);
            this.$carousel.off(features.CANCEL, this.onTapEndHandler);
            if (features.useLeaveInteraction) {
                this.$carousel.off(features.LEAVE, this.onTapEndHandler);
            }
            this.$carousel.off('dragstart', 'img', this.onImageDragHandler);

            return this;
        };

        /**
         * Begins the scroll transaction
         *
         * @method startScroll
         * @chainable
         */
        CarouselView.prototype.startScroll = function() {
            this.isScrolling = true;
            this.isDraggingImage = false;
            this.$carouselList.stop();
            // gather the current carousel list margin

            return this;
        };

        /**
         * Finishes the scroll transaction and unsets any internal properties
         *
         * @method finishScroll
         * @chainable
         */
        CarouselView.prototype.finishScroll = function() {
            this.scrollOffset = 0;
            this.scrollStartOffset = 0;

            this.isScrolling = false;
            this.updateControllers();

            return this;
        };

        /**
         * Updates the current scrolling carousel position based on the current slide
         *
         * @method updateCarouselViewPosition
         * @chainable
         */
        CarouselView.prototype.updateCarouselViewPosition = function() {
            var positionName = 'margin-left';
            if (this.shouldUseRTL) {
                positionName = 'margin-right';
            }

            _positionCache[positionName] = this.getXTargetOffset();
            this.$carouselList.css(_positionCache);
        };

        /**
         * Calculates the X offset of the currently selected slide
         *
         * @method getCurrentSlideOffset
         * @return {number} offset
         */
        CarouselView.prototype.getCurrentSlideOffset = function() {
            return -this.slideWidth * this.currentSlide;
        };

        /**
         * Calculates the X offset of the currently selected slide with current interaction offset
         *
         * @method getXTargetOffset
         * @return {number} offset
         */
        CarouselView.prototype.getXTargetOffset = function() {
            var scrollOffset = this.scrollOffset;
            if (this.shouldUseRTL) {
                scrollOffset = -scrollOffset;
            }
            return this.getCurrentSlideOffset() + scrollOffset;
        };

        /**
         * Retrieves the immediate calculated position relative to where it should be based on the current slide
         *
         * @method getXOffsetFromCurrentSlide
         * @return {number} offset
         */
        CarouselView.prototype.getXOffsetFromCurrentSlide = function() {
            var positionName = 'margin-left';
            if (this.shouldUseRTL) {
                positionName = 'margin-right';
            }

            var targetPosition = this.getCurrentSlideOffset();
            var currentPosition = parseFloat(this.$carouselList.css(positionName));

            return targetPosition - currentPosition;
        };

        /**
         * onTapStart
         * Activates the carousel and tracks the interaction
         *
         * @method onTapStart
         * @param {jQuery.Event} event
         */
        CarouselView.prototype.onTapStart = function(event) {
            if (this.isActive) {
                return;
            }

            this.scrollStartOffset = this.getXOffsetFromCurrentSlide();

            switch (features.interactionProfile) {
                case features.PROFILES.DESKTOP:
                    this.tapX = event.clientX;
                    this.tapY = event.clientY;
                    break;
                case features.PROFILES.MOBILE:
                    var touchMobile = event.originalEvent.touches[0];
                    this.tapX = touchMobile.clientX;
                    this.tapY = touchMobile.clientY;
                    break;
                    // UNTESTED POINTER INTERACTION
                    // This will likely break when using pointers
                    // MOBILE_IE may be the wrong profile name
                    // Not certain as to testing platforms, this NEEDS to be tested
                case features.PROFILES.MOBILE_IE:
                    var touch = event.originalEvent.touches[0];
                    this.tapX = touch.clientX;
                    this.tapY = touch.clientY;
                    break;
            }

            this.activate();

            // Kill the animation if its animating
            if (this.isAnimating) {
                this.$carouselList.stop();
            }

            this.scrollOffset = -this.scrollStartOffset;
            this.updateCarouselViewPosition();
        };

        /**
         * onTapMove
         * Determines and triggers when a scroll has started
         *
         * @method onTapMove
         * @param {jQuery.Event} event
         */
        CarouselView.prototype.onTapMove = function(event) {
            if (!this.isActive) {
                return;
            }

            var deltaX;
            //var deltaY;

            switch (features.interactionProfile) {
                case features.PROFILES.DESKTOP:
                    deltaX = event.clientX - this.tapX;
                    //deltaY = event.clientY - this.tapY;
                    break;
                case features.PROFILES.MOBILE:
                    var touchMobile = event.originalEvent.touches[0];
                    deltaX = touchMobile.clientX - this.tapX;
                    //deltaY = touch.clientY - this.tapY;
                    break;
                    // UNTESTED POINTER INTERACTION
                    // This will likely break when using pointers
                    // MOBILE_IE may be the wrong profile name
                    // Not certain as to testing platforms, this NEEDS to be tested
                case features.PROFILES.MOBILE_IE:
                    var touch = event.originalEvent.touches[0];
                    deltaX = touch.clientX - this.tapX;
                    //deltaY = touch.clientY - this.tapY;
                    break;
            }

            if (Math.abs(deltaX) > SCROLL_X_THRESHOLD) {
                this.startScroll();
            }

            if (!this.isScrolling) {
                return;
            }

            //needed for IE Support
            if (event.preventDefault) {
                event.preventDefault();
            } else {
                event.returnValue = false;
            }
            event.stopPropagation();
            this.scrollOffset = deltaX - this.scrollStartOffset;
            this.updateCarouselViewPosition();
        };

        /**
         * onTapEnd
         * Updates the carousel with the new slide if one is necessary
         *
         * @method onTapEnd
         * @param {jQuery.Event} event
         */
        CarouselView.prototype.onTapEnd = function(event) {
            this.deactivate();

            if (!this.isScrolling) {
                this.finishScroll();
                this.updateCarouselViewPosition();
                return;
            }

            //needed for IE Support
            if (event.preventDefault) {
                event.preventDefault();
            } else {
                event.returnValue = false;
            }
            event.stopPropagation();

            var deltaX;

            switch (features.interactionProfile) {
                case features.PROFILES.DESKTOP:
                    deltaX = event.clientX - this.tapX - this.scrollStartOffset;
                    break;
                case features.PROFILES.MOBILE:
                    var touchMobile = event.originalEvent.changedTouches[0];
                    deltaX = touchMobile.clientX - this.tapX - this.scrollStartOffset;
                    break;
                    // UNTESTED POINTER INTERACTION
                    // This will likely break when using pointers
                    // MOBILE_IE may be the wrong profile name
                    // Not certain as to testing platforms, this NEEDS to be tested
                case features.PROFILES.MOBILE_IE:
                    var touch = event.originalEvent.changedTouches[0];
                    deltaX = touch.clientX - this.tapX;
                    break;
            }

            this.finishScroll();
            if (Math.abs(deltaX) >= this.nextThreshold) {
                if (this.shouldUseRTL) {
                    if (deltaX > 0) {
                        this.scrollToNextSlide();
                    } else {
                        this.scrollToPreviousSlide();
                    }
                } else if (deltaX < 0) {
                    this.scrollToNextSlide();
                } else {
                    this.scrollToPreviousSlide();
                }
            } else {
                this.goToSlide(this.$slides.eq(this.currentSlide).id);
            }
        };

        /**
         * onImageDrag
         * Stops any dragging of the image if the carousel is already scrolling
         * Prevents any scrolling once a image drag has begun
         *
         * @method onImageDrag
         * @param {jQuery.Event} event
         */
        CarouselView.prototype.onImageDrag = function(event) {
            if (!this.isScrolling) {
                this.isDraggingImage = true;
                this.deactivate();
                this.finishScroll();
                return this;
            }

            //needed for IE Support
            if (event.preventDefault) {
                event.preventDefault();
            } else {
                event.returnValue = false;
            }
            event.stopPropagation();
        };

        /**
         * Scrolls to the next slide if possible, otherwise allows the structure to readjust to the original
         *
         * @method scrollToNextSlide
         * @param {jQuery.Event} event
         * @chainable
         */
        CarouselView.prototype.scrollToNextSlide = function() {
            if (this.currentSlide + 1 >= this.slideCount) {
                this.goToSlide(this.$slides.eq(this.currentSlide).id);

                return this;
            }

            this.nextSlide();

            return this;
        };

        /**
         * Scrolls to the next slide if possible, otherwise allows the structure to readjust to the original
         *
         * @method scrollToNextSlide
         * @param {jQuery.Event} event
         * @chainable
         */
        CarouselView.prototype.scrollToPreviousSlide = function() {
            if (this.currentSlide - 1 < 0) {
                this.goToSlide(this.$slides.eq(this.currentSlide).id);

                return this;
            }

            this.previousSlide();

            return this;
        };

        return CarouselView;
    });
}(MMMRequire));
